import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getEditParty,
  getIsPartyModalOpen,
  togglePartyModal,
} from "../../features/parties/partiesSlice";
import {
  fetchProcessDefinitions,
  getProcessDefinitions,
} from "../../features/processDefinitions/processDefinitionsSlice";
import { IFormInput } from "../../types/parties";
import { localizedProcessName } from "../../lib/utils";
import { t } from "../../locales";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

const multiSelectStyles: any = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: "48px",
    borderRadius: "0.75rem",
    borderWidth: "2px",
    borderColor: state.isFocused ? "#113F67" : "#e5e7eb",
    boxShadow: state.isFocused
      ? "0 0 0 2px #fff, 0 0 0 4px #113F67"
      : "none",
    paddingLeft: "0.5rem",
    "&:hover": {
      borderColor: state.isFocused ? "#113F67" : "#e5e7eb",
    },
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "#9ca3af",
  }),
  singleValue: (base: any) => ({
    ...base,
    color: "#11204d",
  }),
  option: (base: any, { isSelected }: any) => ({
    ...base,
    color: "#11204d",
    backgroundColor: isSelected ? "#e5e7eb" : base.backgroundColor,
  }),
  multiValue: (base: any) => ({
    ...base,
    backgroundColor: "#e8f0f7",
    borderRadius: "0.375rem",
  }),
  multiValueLabel: (base: any) => ({
    ...base,
    color: "#113F67",
    fontWeight: 500,
  }),
  multiValueRemove: (base: any) => ({
    ...base,
    color: "#113F67",
    ":hover": {
      backgroundColor: "#113F67",
      color: "white",
    },
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 60,
  }),
};

type Props = {
  handleSaveParty: (data: IFormInput) => void;
};

const PartyFormModal = ({ handleSaveParty }: Props) => {
  const dispatch = useAppDispatch();
  const editParty = useAppSelector(getEditParty);
  const isPartyModalOpen = useAppSelector(getIsPartyModalOpen);
  const definitions = useAppSelector(getProcessDefinitions);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const processOptions = definitions.map((d) => ({
    value: d.id,
    label: localizedProcessName(d),
  }));

  const {
    handleSubmit,
    register,
    reset,
    control,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: {
      firm_name: "",
      name: "",
      process_definition_ids: [],
      phone: "",
      wp_phone: "",
      address: "",
    },
  });

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    setIsLoading(true);
    await handleSaveParty(data);
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(togglePartyModal(false));
  };

  useEffect(() => {
    if (definitions.length === 0) {
      dispatch(fetchProcessDefinitions({ all: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (editParty && editParty.id) {
      reset({
        firm_name: editParty.firm_name,
        name: editParty.name,
        process_definition_ids: (editParty.process_definitions || []).map((d) => d.id),
        phone: editParty.phone,
        wp_phone: editParty.wp_phone,
        address: editParty.address,
      });
    } else {
      reset({
        firm_name: "",
        name: "",
        process_definition_ids: [],
        phone: "",
        wp_phone: "",
        address: "",
      });
    }
  }, [editParty, isPartyModalOpen]);

  return (
    <Dialog
      open={isPartyModalOpen}
      onOpenChange={(open) => {
        if (!open) handleCloseFormModal();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#113F67]">
            {t.parties.formTitle}
          </DialogTitle>
          <DialogDescription>
            {editParty
              ? `Editing: ${editParty.firm_name}`
              : t.parties.addNewParty}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-4 flex-1 flex flex-col sm:block pb-16 sm:pb-0 sm:space-y-5">
          <div className="space-y-2">
            <Label htmlFor="firm_name" className="text-sm font-semibold text-[#113F67]">
              {t.parties.firmNameLabel} <span className="text-red-500">*</span>
            </Label>
            <Input
              id="firm_name"
              placeholder={t.parties.firmNamePlaceholder}
              {...register("firm_name", { required: true })}
              className="h-11"
            />
            {errors.firm_name && errors.firm_name.type === "required" && (
              <span className="text-red-500 text-sm">
                {t.parties.firmNameRequired}
              </span>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-semibold text-[#113F67]">
              {t.parties.typeLabel} <span className="text-red-500">*</span>
            </Label>
            <Controller
              name="process_definition_ids"
              control={control}
              rules={{ validate: (val) => (Array.isArray(val) && val.length > 0) || t.parties.typeRequired }}
              render={({ field }) => (
                <ReactSelect
                  isMulti
                  styles={multiSelectStyles}
                  placeholder={t.parties.typePlaceholder}
                  options={processOptions}
                  value={processOptions.filter((opt) => field.value?.includes(opt.value))}
                  onChange={(selected: any) => {
                    field.onChange(selected ? selected.map((opt: any) => opt.value) : []);
                  }}
                />
              )}
            />
            {errors.process_definition_ids && (
              <span className="text-red-500 text-sm">
                {t.parties.typeRequired}
              </span>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">{t.parties.nameLabel}</Label>
            <Input
              id="name"
              placeholder={t.parties.namePlaceholder}
              {...register("name")}
              className="h-11"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">{t.parties.phoneLabel}</Label>
            <Input
              id="phone"
              placeholder={t.parties.phonePlaceholder}
              {...register("phone")}
              className="h-11"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="wp_phone">{t.parties.wpPhoneLabel}</Label>
            <Input
              id="wp_phone"
              placeholder={t.parties.wpPhonePlaceholder}
              {...register("wp_phone")}
              className="h-11"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">{t.parties.addressLabel}</Label>
            <Textarea
              id="address"
              rows={3}
              placeholder={t.parties.addressPlaceholder}
              {...register("address")}
            />
          </div>

          <div className="fixed bottom-0 left-0 right-0 flex gap-3 p-4 justify-end border-t bg-white z-10 sm:static sm:p-0 sm:pt-2 sm:z-auto sm:border-t sm:bg-transparent">
            <Button
              type="button"
              variant="outline"
              className="h-11 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
              onClick={handleCloseFormModal}
              disabled={isLoading}
            >
              {t.parties.cancelButton}
            </Button>
            <Button
              type="submit"
              className="h-11 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
              disabled={isLoading}
            >
              {isLoading ? t.parties.saving : t.parties.saveButton}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PartyFormModal;
